// ============================================================
// combatantRosterMerge - fold a decided remote view into the roster.
//
// Pure: this is the body of useCombatantSync's `setRoster` updater, and
// a state updater MUST be pure - React invokes it more than once. Every
// decision (and every echo-cache write) is made before it, outside; see
// combatantSyncRead.ts.
// ============================================================

import type { Combatant } from '../engine/roster/types';

/** What the listener decided about one remote combatant, before the updater runs. */
export interface ReadDecision {
  r: Combatant;
  takeState: boolean;
  takeSheet: boolean;
  hasSheet: boolean;
}

/** One remote view, decided. */
export interface ReadPlan {
  decisions: Map<string, ReadDecision>;
  /** The ids the view lists that this client may use. */
  remoteIds: Set<string>;
  /** Ids the view announces as genuinely removed (`docChanges()`). */
  removedIds: Set<string>;
  /** Whether the view lists everyone - mid-burst it does not. */
  listsEveryone: boolean;
  /** Ids confirmed remotely BEFORE this view. */
  previouslyConfirmed: Set<string>;
}

/**
 * The local combatant with the remote halves it accepted. The cold half
 * travels together; the remote clock is adopted with the sheet, and the
 * roster setter's stamp takes max(), so it can never run backwards.
 */
function mergeHalves(local: Combatant, { r, takeState, takeSheet }: ReadDecision): Combatant {
  const cold = takeSheet ? r : local;
  return {
    ...local,
    state: takeState ? r.state : local.state,
    sheet: cold.sheet,
    overrides: cold.overrides,
    name: cold.name,
    kind: cold.kind,
    // order travels in the cold half both ways (coldFields, remoteCold).
    // Keeping our own while the echo guard cached theirs had every screen
    // writing its order back at the others for ever (v8.393.7, ./dev fuzz).
    order: cold.order,
    slotOrder: cold.slotOrder,
    loadoutRef: cold.loadoutRef,
    sheetRev: cold.sheetRev,
  };
}

/**
 * The row a decision produces, or null for none.
 *
 * A DOCUMENT WITH NO SHEET MUST NEVER BECOME A COMBATANT. `combatantFromDoc`
 * fabricates an empty Character for a document that has state but no sheet -
 * the window between the 350 ms state write and the 700 ms sheet write. The
 * read filter already refuses those, but it asks the ref'd roster while
 * this asks `prev`, and the two are not the same view. Pushed AS the
 * combatant, the fabrication was written back over the real one by this
 * client's own sheet sync - "New Character", `kind` empty, order 0, seen in
 * a live session. Skipping costs nothing: the sheet write brings it.
 */
function decidedRow(local: Combatant | undefined, d: ReadDecision): Combatant | null {
  if (!local) return d.hasSheet ? d.r : null;
  if (!d.takeState && !d.takeSheet) return local;
  return mergeHalves(local, d);
}

/**
 * A local the view does not mention is kept - unless it is announced as
 * removed, or it was confirmed remotely before and is absent from a
 * COMPLETE view (somebody deleted it). Absent from a partial view means
 * nothing at all (v8.22.0).
 */
function keepsUnmentioned(id: string, plan: ReadPlan): boolean {
  if (plan.remoteIds.has(id) || plan.removedIds.has(id)) return false;
  return !(plan.listsEveryone && plan.previouslyConfirmed.has(id));
}

/**
 * The locals this view takes off the roster: the departures this screen
 * HEARD rather than made (2026-10-06). The listener notes them before the
 * updater runs, so the departure effect does not send their delete again.
 */
export function departedIn(roster: readonly Combatant[], plan: ReadPlan): string[] {
  return roster.filter((c) => !plan.remoteIds.has(c.id) && !keepsUnmentioned(c.id, plan)).map((c) => c.id);
}

/**
 * ALWAYS by `order`, never by snapshot order: Firestore returns documents
 * in DOCUMENT ID order, so rebuilding from the snapshot moved `cbt-7`
 * ahead of `tab-1`, and the legacy renumbering then fought it in a loop.
 * The id tiebreak keeps ties deterministic, or two sharing an order
 * oscillate between snapshots and never settle.
 */
const byOrder = (a: Combatant, b: Combatant) => a.order - b.order || a.id.localeCompare(b.id);

/**
 * The roster after this view: decided rows in view order, then the kept
 * unmentioned locals in their own order, all sorted. Returns `prev` itself
 * when nothing changed, so an unchanged view does not re-render.
 */
export function mergeRead(prev: Combatant[], plan: ReadPlan): Combatant[] {
  const byId = new Map(prev.map((c) => [c.id, c]));
  const next: Combatant[] = [];
  for (const [id, d] of plan.decisions) {
    const row = decidedRow(byId.get(id), d);
    if (row) next.push(row);
  }
  const kept = prev.filter((c) => keepsUnmentioned(c.id, plan));
  const result = [...next, ...kept].sort(byOrder);
  const unchanged = result.length === prev.length && result.every((c, i) => c === prev[i]);
  return unchanged ? prev : result;
}
